import React, { useState, useEffect } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';

/* ══════════════════════════════════════════════════════════════════════════
   GET STARTED — RevUpAI  (v2 — mobile/tablet responsive)
   ══════════════════════════════════════════════════════════════════════════ */

const C   = '#00D4AA';
const R   = '0,212,170';
const BG  = '#09060F';

/* ── Breakpoint hook ──────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1024 };
}

/* ── Helpers ──────────────────────────────────────────────────────────── */
const Section = ({ children, style = {} }) => (
  <section style={{ padding:`${SECTION_PAD} 0`, position:'relative', ...style }}>{children}</section>
);
const Cap = ({ children, style = {} }) => (
  <div style={{ ...WRAP, ...style }}>{children}</div>
);
const Eyebrow = ({ children, color = C }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color, margin:'0 0 14px', opacity:.85 }}>{children}</p>
);

/* ══════════════════════════════════════════════════════════════════════════
   HERO — centred, single column ✓
   ══════════════════════════════════════════════════════════════════════════ */
function Hero() {
  const { mob } = useBreakpoint();
  return (
    <section style={{ background:BG, padding:`${SECTION_PAD} 0 clamp(60px,7vh,80px)`, position:'relative', overflow:'hidden', textAlign:'center' }}>
      <div aria-hidden="true" style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:900, height:900, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.07) 0%,transparent 65%)`, filter:'blur(120px)' }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.012) 1px,transparent 1px)', backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse 80% 60% at 50% 40%,black 0%,transparent 100%)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:2, maxWidth:780 }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 16px 6px 10px', borderRadius:100, marginBottom:32, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, animation:'fadeUp .7s ease both .1s' }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:C }}/>
          <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:600, color:`rgba(${R},.85)`, letterSpacing:.3 }}>Getting Started</span>
        </div>

        <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, lineHeight:1.08, letterSpacing:'-2.5px', fontSize:'clamp(36px,4.5vw,68px)', color:'#EDE9F8', margin:'0 0 24px', animation:'fadeUp .8s ease both .2s' }}>
          Pre-built AI roles.<br/><span style={{ color:C }}>Live in days, not months.</span>
        </h1>

        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 auto 32px', maxWidth:620, animation:'fadeUp .7s ease both .32s' }}>
          Our pre-built AI roles are fully productised and ready to deploy — agent architecture, domain expertise, and operating model already built. For organisations with workflows unique to their business, we also design and operate custom AI roles through a structured Discovery engagement.
        </p>

        <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', padding: mob?'0 16px':'0', animation:'fadeUp .7s ease both .42s' }}>
          <a href="/ai-ciso" style={{ padding:'13px 28px', borderRadius:9, cursor:'pointer', background:C, color:'#09060F', border:'none', fontSize:14, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', boxShadow:`0 6px 24px rgba(${R},.35)`, transition:'all .22s ease', display:'inline-block', flex: mob?'1 1 auto':'none', textAlign:'center' }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 10px 32px rgba(${R},.5)`;}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 6px 24px rgba(${R},.35)`;}}
          >Explore Pre-Built Roles →</a>
          <a href="/custom" style={{ padding:'13px 24px', borderRadius:9, cursor:'pointer', background:'transparent', color:'#C8C0EC', border:'1px solid rgba(255,255,255,.12)', fontSize:14, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', transition:'all .22s ease', display:'inline-block', flex: mob?'1 1 auto':'none', textAlign:'center' }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.28)';e.currentTarget.style.color='rgba(255,255,255,.85)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.12)';e.currentTarget.style.color='rgba(255,255,255,.55)';}}
          >Custom AI Roles</a>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   TWO TRACKS
   ══════════════════════════════════════════════════════════════════════════ */
function TwoTracks() {
  const { mob, tab } = useBreakpoint();

  const customFactors = [
    { icon:'🔗', label:'Workflow Complexity', desc:'Single-workflow agents with well-defined inputs differ significantly from multi-agent systems spanning several enterprise systems.' },
    { icon:'☁️', label:'Infrastructure & Cloud', desc:'AWS, Azure, GCP, on-premise, or multi-cloud — data residency and compliance requirements shape both build and operating cost.' },
    { icon:'🧠', label:'Domain Expertise Required', desc:'The more specialised the HITL decisions — regulatory rulings, financial risk, clinical judgment — the more senior the domain expertise we staff in.' },
    { icon:'📊', label:'Scale & Volume', desc:'An agent handling 10,000 decisions a day has different infrastructure and monitoring requirements to one handling 100.' },
    { icon:'🔒', label:'Compliance Environment', desc:'HIPAA, SOC2, FCA, PCI-DSS — regulated environments require additional architecture, audit tooling, and validation effort scoped explicitly upfront.' },
  ];

  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)` }}>
      <Cap>
        <div style={{ textAlign:'center', marginBottom:52 }}>
          <Eyebrow>How Engagement Works</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(26px,2.6vw,42px)', lineHeight:1.1, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 auto 16px', maxWidth:720 }}>
            Pre-built agents are predictable.<br/>Custom agents are scoped.
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.05vw,16px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:560, margin:'0 auto' }}>
            Two very different engagement models — and it's important to understand which one applies to you.
          </p>
        </div>

        <div style={{ display:'grid', gridTemplateColumns: tab?'1fr':'1fr 1fr', gap: tab?16:2 }}>

          {/* Pre-built */}
          <div style={{ padding: mob?'28px 24px':'40px 36px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.25)`, borderRadius: tab?'10px':'0', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'4px 12px', borderRadius:100, background:`rgba(${R},.1)`, border:`1px solid rgba(${R},.25)`, marginBottom:24, width:'fit-content' }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background:C }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.8)` }}>Pre-Built AI Roles</span>
            </div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(19px,1.8vw,26px)', fontWeight:800, color:'#EDE9F8', margin:'0 0 14px', lineHeight:1.2 }}>The complexity is already solved.</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:'#C8C0EC', margin:'0 0 28px' }}>
              RevSec, RevMigrate, and RevModernize are fully productised AI roles. The agent architecture, domain expertise, HITL framework, and operating model are already built. You're not commissioning a build — you're deploying a proven role into your environment.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:28, flex:1 }}>
              {[
                { label:'Defined scope',         desc:"Each role has a clear, documented capability set — no ambiguity about what you're getting." },
                { label:'Predictable deployment', desc:'2–4 week onboarding to configure for your infrastructure, data sources, and access controls.' },
                { label:'Fixed operating model',  desc:'Domain experts, monitoring, and incident response already built in to every role.' },
                { label:'Faster to value',        desc:'No discovery, no architecture phase — straight to deployment scoping and go-live.' },
              ].map((item,i)=>(
                <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'12px 16px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.15)`, borderRadius:9 }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:C, marginTop:6, flexShrink:0 }}/>
                  <div>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, color:'#C8C0EC' }}>{item.label} — </span>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, color:'#9B90BA' }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:'16px 20px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:9 }}>
              <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:14, fontWeight:300, lineHeight:1.65, color:`rgba(${R},.75)`, margin:0 }}>
                The investment conversation for a pre-built role is straightforward — deployment scope, configuration requirements, and operating tier. We can scope that in a single call.
              </p>
            </div>
          </div>

          {/* Custom */}
          <div style={{ padding: mob?'28px 24px':'40px 36px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.08)', borderRadius: tab?'10px':'0', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'4px 12px', borderRadius:100, background:'rgba(139,92,246,.1)', border:'1px solid rgba(139,92,246,.25)', marginBottom:24, width:'fit-content' }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background:'#8B5CF6' }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(139,92,246,.8)' }}>Custom AI Roles</span>
            </div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(19px,1.8vw,26px)', fontWeight:800, color:'#EDE9F8', margin:'0 0 14px', lineHeight:1.2 }}>Scoped through Discovery.<br/>Priced from there.</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:'#C8C0EC', margin:'0 0 28px' }}>
              Custom AI roles are purpose-built for workflows unique to your organisation. Before we can price one, we need to understand the problem — which is what a Discovery engagement is designed to do. Five factors shape what a custom role costs.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:28, flex:1 }}>
              {customFactors.map((f,i)=>(
                <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'11px 15px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:9, transition:'all .2s ease', cursor:'default' }}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(139,92,246,.06)';e.currentTarget.style.borderColor='rgba(139,92,246,.2)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.03)';e.currentTarget.style.borderColor='rgba(255,255,255,.07)';}}
                >
                  <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{f.icon}</span>
                  <div>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:700, color:'#DDD8F2' }}>{f.label} — </span>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, color:'#9B90BA', lineHeight:1.65 }}>{f.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:'16px 20px', background:'rgba(139,92,246,.07)', border:'1px solid rgba(139,92,246,.2)', borderRadius:9 }}>
              <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:14, fontWeight:300, lineHeight:1.65, color:'rgba(139,92,246,.75)', margin:0 }}>
                A Discovery engagement is fixed-scope and fixed-price. It's the lowest-risk way to understand what a custom AI role would cost — before committing to a build.
              </p>
            </div>
          </div>

        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   THREE PATHS
   ══════════════════════════════════════════════════════════════════════════ */
function ThreePaths() {
  const { mob, tab } = useBreakpoint();

  const paths = [
    {
      n:'01', icon:'⚡', label:'Deploy a Pre-Built AI Role',
      color:'#00D4AA', rgb:'0,212,170',
      headline:'Fastest path to an operated AI role in your enterprise.',
      sub:'RevSec, RevMigrate, and RevModernize are purpose-built, fully operated, and ready to deploy into your environment. Faster to value than a custom build, with the same domain expertise and HITL architecture.',
      includes:['Deployment scoping and environment assessment','Configuration for your infrastructure and data sources','Domain expert staffing for HITL decisions','Production monitoring and incident management','Ongoing optimisation and capability evolution'],
      cta:'Explore AI Roles', href:'/ai-ciso',
      note:'Typical deployment: 2–4 weeks from scoping to live.',
    },
    {
      n:'02', icon:'🔍', label:'Start with a Discovery',
      color:'#8B5CF6', rgb:'139,92,246',
      headline:"The right starting point when you know AI can help but not exactly how.",
      sub:"A structured 2-week engagement to identify your highest-value AI opportunity, validate the business case, and produce a clear recommendation — before any build commitment. We tell you what's worth building and what isn't.",
      includes:['Structured interviews across engineering, operations, and leadership','Value analysis: frequency × effort × error rate — ranked by ROI','Technical feasibility: data, access, integration, compliance assessment','AI opportunity map with prioritised build recommendation','Honest assessment of what to build now vs. what to defer'],
      cta:'Start a Discovery', href:'/custom',
      note:'Fixed-scope engagement. Deliverable: AI opportunity map + recommendation.',
      highlight: true,
    },
    {
      n:'03', icon:'🏗️', label:'Build a Custom AI Role',
      color:'#7C3AED', rgb:'124,58,237',
      headline:'A custom AI role built and operated for a workflow unique to your organisation.',
      sub:'For organisations with a clear problem and a validated business case. We design, build, and operate a purpose-built AI role — with domain experts in the loop — inside your environment.',
      includes:['Full Discovery engagement to confirm scope and business case','Agent architecture and technical design','Production build with testing, CI/CD, and observability','Domain expert staffing for human-in-the-loop decisions','Operated in production with SLA-backed monitoring'],
      cta:'Talk to an AI Architect', href:'/contact',
      note:'Typically follows a Discovery engagement. Timeline varies by scope.',
    },
  ];

  // Desktop: 3-col | Tablet: 2-col (Discovery highlighted in middle, then 3rd below) | Mobile: 1-col
  const cols = mob ? '1fr' : tab ? '1fr 1fr' : 'repeat(3,1fr)';

  return (
    <Section style={{ background:BG }}>
      <Cap>
        <div style={{ textAlign:'center', marginBottom:52 }}>
          <Eyebrow>Three Ways to Start</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(26px,2.6vw,42px)', lineHeight:1.1, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 auto 16px', maxWidth:680 }}>
            Choose the right entry point<br/>for where you are today.
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.05vw,16px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:520, margin:'0 auto' }}>
            Not every organisation is at the same stage of AI readiness. These three paths reflect the three most common starting points we see.
          </p>
        </div>

        <div style={{ display:'grid', gridTemplateColumns: cols, gap: mob?16:2 }}>
          {paths.map((p,i)=>(
            <div key={i} style={{ display:'flex', flexDirection:'column', background:p.highlight?`rgba(${p.rgb},.07)`:'rgba(255,255,255,.025)', border:`1px solid ${p.highlight?`rgba(${p.rgb},.3)`:'rgba(255,255,255,.07)'}`, position:'relative', borderRadius: mob?'10px':'0' }}>
              {p.highlight && (
                <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', padding:'3px 16px', borderRadius:100, background:p.color, fontSize:10, fontWeight:800, letterSpacing:1.5, textTransform:'uppercase', color:'#09060F', whiteSpace:'nowrap' }}>Recommended Starting Point</div>
              )}
              <div style={{ padding: mob?'28px 24px':'32px 28px', flex:1, display:'flex', flexDirection:'column' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                  <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:12, fontWeight:700, color:`rgba(${p.rgb},.4)`, letterSpacing:1 }}>{p.n}</span>
                  <div style={{ width:36, height:36, borderRadius:10, background:`rgba(${p.rgb},.12)`, border:`1px solid rgba(${p.rgb},.25)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>{p.icon}</div>
                </div>
                <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${p.rgb},.7)`, marginBottom:10 }}>{p.label}</div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(16px,1.4vw,19px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 12px', lineHeight:1.3 }}>{p.headline}</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:'0 0 24px' }}>{p.sub}</p>

                <div style={{ marginBottom:24, flex:1 }}>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(200,192,236,0.4)', margin:'0 0 10px' }}>What's included</p>
                  {p.includes.map((item,j)=>(
                    <div key={j} style={{ display:'flex', gap:9, alignItems:'flex-start', marginBottom:7 }}>
                      <div style={{ width:4, height:4, borderRadius:'50%', background:p.color, marginTop:6, flexShrink:0, opacity:.7 }}/>
                      <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, lineHeight:1.65, color:'#9B90BA' }}>{item}</span>
                    </div>
                  ))}
                </div>

                <div style={{ padding:'10px 14px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.06)', borderRadius:7, marginBottom:20 }}>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11.5, fontStyle:'italic', color:'rgba(200,192,236,0.55)', margin:0 }}>{p.note}</p>
                </div>

                <a href={p.href} style={{ display:'block', padding:'13px 20px', borderRadius:9, cursor:'pointer', background:p.highlight?p.color:'transparent', color:p.highlight?'#09060F':`rgba(${p.rgb},.85)`, border:`1px solid ${p.highlight?'transparent':`rgba(${p.rgb},.35)`}`, fontSize:13, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", textAlign:'center', textDecoration:'none', transition:'all .2s ease', boxShadow:p.highlight?`0 6px 24px rgba(${p.rgb},.35)`:'none' }}
                  onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.background=p.highlight?p.color:`rgba(${p.rgb},.1)`;e.currentTarget.style.boxShadow=`0 10px 28px rgba(${p.rgb},.4)`;}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.background=p.highlight?p.color:'transparent';e.currentTarget.style.boxShadow=p.highlight?`0 6px 24px rgba(${p.rgb},.35)`:'none';}}
                >{p.cta} →</a>
              </div>
            </div>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   QUALIFIER FORM — already single column, just tighten mobile padding
   ══════════════════════════════════════════════════════════════════════════ */
function QualifierForm() {
  const { mob } = useBreakpoint();
  const [step, setStep]       = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const questions = [
    {
      id:'path', q:"What best describes where you are today?",
      options:[
        { value:'deploy',    label:'I want to deploy a pre-built AI role',         sub:'RevSec, RevMigrate, or RevModernize' },
        { value:'discover',  label:'I know AI can help but need to identify where', sub:'Start with a Discovery engagement' },
        { value:'build',     label:'I have a specific workflow I want to automate', sub:'Custom AI role build' },
        { value:'exploring', label:"I'm exploring what's possible",                sub:'Not ready to commit yet' },
      ],
    },
    {
      id:'industry', q:'Which industry are you in?',
      options:[
        { value:'bfsi',       label:'Banking, Financial Services & Insurance', sub:'' },
        { value:'retail',     label:'Retail & Commerce',                       sub:'' },
        { value:'highered',   label:'Higher Education',                        sub:'' },
        { value:'healthcare', label:'Healthcare',                               sub:'' },
        { value:'tech',       label:'Technology / SaaS',                       sub:'' },
        { value:'other',      label:'Other',                                   sub:'We work across many sectors' },
      ],
    },
    {
      id:'scale', q:'How large is your organisation?',
      options:[
        { value:'smb',        label:'Under 500 employees',      sub:'' },
        { value:'midmarket',  label:'500 – 5,000 employees',    sub:'' },
        { value:'enterprise', label:'5,000 – 50,000 employees', sub:'' },
        { value:'large',      label:'50,000+ employees',        sub:'' },
      ],
    },
    {
      id:'timeline', q:"What's your timeline for getting started?",
      options:[
        { value:'now',      label:'Immediately',      sub:'Ready to start a conversation this week' },
        { value:'quarter',  label:'This quarter',     sub:'Within the next 3 months' },
        { value:'planning', label:'Next 6 months',    sub:'Still in planning phase' },
        { value:'research', label:'Just researching', sub:'No immediate timeline' },
      ],
    },
  ];

  const current = questions[step];

  const select = (value) => {
    const updated = { ...answers, [current.id]: value };
    setAnswers(updated);
    if (step < questions.length - 1) {
      setTimeout(() => setStep(s => s + 1), 280);
    } else {
      setTimeout(() => setSubmitted(true), 280);
    }
  };

  const pathLabels = {
    deploy:'deploy a pre-built AI role', discover:'start with a Discovery',
    build:'build a custom AI role', exploring:"explore what's possible",
  };
  const timelineLabels = {
    now:'immediately', quarter:'this quarter', planning:'in the next 6 months', research:'just researching for now',
  };

  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)` }}>
      <Cap style={{ maxWidth:820 }}>
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <Eyebrow>Let's Find the Right Conversation</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(24px,2.4vw,38px)', lineHeight:1.1, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 auto 14px', maxWidth:600 }}>
            Four questions. Then we'll reach out.
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(13px,1.05vw,15px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:480, margin:'0 auto' }}>
            No pricing calculator. Just enough context for our team to have a useful first conversation with you — rather than starting from zero.
          </p>
        </div>

        {!submitted ? (
          <div>
            {/* Progress */}
            <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:36 }}>
              {questions.map((_,i) => (
                <div key={i} style={{ height:3, width:i===step?32:20, borderRadius:100, background:i<=step?C:'rgba(255,255,255,.1)', opacity:i<=step?1:.4, transition:'all .3s ease' }}/>
              ))}
            </div>

            {/* Question */}
            <div key={step} style={{ animation:'fadeIn .3s ease both' }}>
              <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(17px,1.6vw,22px)', fontWeight:700, color:'#D4CEF0', textAlign:'center', margin:'0 0 28px', lineHeight:1.4 }}>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.6)`, display:'block', marginBottom:10 }}>Question {step+1} of {questions.length}</span>
                {current.q}
              </p>

              <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(${mob?'100%':'220px'},1fr))`, gap:10 }}>
                {current.options.map((opt,i) => {
                  const selected = answers[current.id] === opt.value;
                  return (
                    <button key={i} onClick={()=>select(opt.value)} style={{ padding:'18px 20px', borderRadius:10, cursor:'pointer', background:selected?`rgba(${R},.12)`:'rgba(255,255,255,.03)', border:`1px solid ${selected?`rgba(${R},.4)`:'rgba(255,255,255,.08)'}`, textAlign:'left', transition:'all .18s ease', transform:selected?'scale(1.01)':'none' }}
                      onMouseEnter={e=>{if(!selected){e.currentTarget.style.background=`rgba(${R},.06)`;e.currentTarget.style.borderColor=`rgba(${R},.2)`;e.currentTarget.style.transform='translateY(-1px)';}}}
                      onMouseLeave={e=>{if(!selected){e.currentTarget.style.background='rgba(255,255,255,.03)';e.currentTarget.style.borderColor='rgba(255,255,255,.08)';e.currentTarget.style.transform='none';}}}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:14, height:14, borderRadius:'50%', border:`1.5px solid ${selected?C:'rgba(255,255,255,.2)'}`, background:selected?C:'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', transition:'all .18s' }}>
                          {selected && <div style={{ width:5, height:5, borderRadius:'50%', background:'#09060F' }}/>}
                        </div>
                        <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:selected?700:400, color:selected?'#EDE9F8':'#C8C0EC', transition:'all .18s' }}>{opt.label}</span>
                      </div>
                      {opt.sub && <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:300, color:'rgba(200,192,236,0.5)', margin:'6px 0 0 24px' }}>{opt.sub}</p>}
                    </button>
                  );
                })}
              </div>
            </div>

            {step > 0 && (
              <div style={{ textAlign:'center', marginTop:24 }}>
                <button onClick={()=>setStep(s=>s-1)} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:'rgba(200,192,236,0.45)', padding:'6px 12px' }}>← Back</button>
              </div>
            )}
          </div>
        ) : (
          <div key="done" style={{ textAlign:'center', padding:'52px 0', animation:'fadeIn .5s ease both' }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:`rgba(${R},.12)`, border:`1px solid rgba(${R},.3)`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px', fontSize:24 }}>✓</div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(20px,2vw,28px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 14px' }}>We'll be in touch shortly.</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:460, margin:'0 auto 28px' }}>
              Based on your answers, we'll reach out with the right person for the conversation — not a generic sales call.
            </p>
            <div style={{ display:'inline-flex', flexDirection:'column', gap:8, padding:'20px 28px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:12, textAlign:'left', minWidth: mob?'auto':'320px', width: mob?'100%':'auto' }}>
              {answers.path     && <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#9B90BA' }}>Looking to <strong style={{ color:`rgba(${R},.8)`, fontWeight:600 }}>{pathLabels[answers.path]}</strong></div>}
              {answers.industry && <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#9B90BA' }}>Industry: <strong style={{ color:'#C8C0EC', fontWeight:600 }}>{questions[1].options.find(o=>o.value===answers.industry)?.label}</strong></div>}
              {answers.scale    && <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#9B90BA' }}>Organisation: <strong style={{ color:'#C8C0EC', fontWeight:600 }}>{questions[2].options.find(o=>o.value===answers.scale)?.label}</strong></div>}
              {answers.timeline && <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#9B90BA' }}>Timeline: <strong style={{ color:'#C8C0EC', fontWeight:600 }}>{timelineLabels[answers.timeline]}</strong></div>}
            </div>
          </div>
        )}
      </Cap>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CLOSING BANNER
   ══════════════════════════════════════════════════════════════════════════ */
function ClosingBanner() {
  const { mob, tab } = useBreakpoint();

  const upcoming = [
    { icon:'🏦', label:'AI Risk Analyst',           domain:'BFSI' },
    { icon:'🛒', label:'AI Merchandising Agent',    domain:'Retail' },
    { icon:'🎓', label:'AI Student Services Agent', domain:'Higher Education' },
    { icon:'⚖️', label:'AI Compliance Agent',       domain:'Legal & Compliance' },
  ];

  return (
    <Section style={{ background:BG }}>
      <Cap>
        <div style={{ display:'grid', gridTemplateColumns: tab?'1fr':'1fr 1fr', gap: tab?16:2 }}>

          {/* Pre-built — go live fast */}
          <div style={{ padding: mob?'28px 24px':'44px 40px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.25)`, borderRadius: tab?'10px':'12px 0 0 12px' }}>
            <Eyebrow>Pre-Built AI Roles</Eyebrow>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2vw,32px)', lineHeight:1.2, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 0 16px' }}>
              Go live in days.<br/>Start getting value immediately.
            </h2>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:'#C8C0EC', margin:'0 0 28px' }}>
              Our pre-built AI roles — RevSec, RevMigrate, RevModernize — are fully productised and ready to deploy into your environment. No lengthy build phase. No complex scoping. Operated AI running inside your enterprise within days, not months.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:32 }}>
              {[
                'Agent architecture already built and production-tested',
                'Domain experts staffed and ready from day one',
                'Deployed on your cloud infrastructure in days',
                'Value from week one — not quarter three',
              ].map((t,i)=>(
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:C, marginTop:6, flexShrink:0 }}/>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.65, color:'#C8C0EC' }}>{t}</span>
                </div>
              ))}
            </div>
            <a href="/ai-ciso" style={{ display: mob?'block':'inline-block', padding:'13px 28px', borderRadius:9, cursor:'pointer', background:C, color:'#09060F', border:'none', fontSize:14, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', boxShadow:`0 6px 24px rgba(${R},.35)`, transition:'all .22s ease', textAlign:'center' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 10px 32px rgba(${R},.5)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 6px 24px rgba(${R},.35)`;}}
            >Explore Pre-Built AI Roles →</a>
          </div>

          {/* Growing portfolio */}
          <div style={{ padding: mob?'28px 24px':'44px 40px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.08)', borderLeft: tab?undefined:'none', borderRadius: tab?'10px':'0 12px 12px 0' }}>
            <Eyebrow color='rgba(139,92,246,.8)'>Growing Portfolio</Eyebrow>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2vw,32px)', lineHeight:1.2, letterSpacing:'-1px', color:'#EDE9F8', margin:'0 0 16px' }}>
              More AI roles.<br/>More industries. Coming soon.
            </h2>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:'#C8C0EC', margin:'0 0 28px' }}>
              We're expanding the pre-built portfolio across BFSI, Retail, Higher Education, and beyond — every new role productised with the same domain expertise and Operated AI model.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:32 }}>
              {upcoming.map((item,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'11px 16px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:9 }}>
                  <span style={{ fontSize:16 }}>{item.icon}</span>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:500, color:'#C8C0EC', flex:1 }}>{item.label}</span>
                  <span style={{ padding:'2px 9px', borderRadius:100, background:'rgba(139,92,246,.1)', border:'1px solid rgba(139,92,246,.2)', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(139,92,246,.65)', flexShrink:0 }}>{item.domain}</span>
                  <span style={{ padding:'2px 9px', borderRadius:100, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9, fontWeight:700, letterSpacing:1, color:'rgba(200,192,236,0.4)', textTransform:'uppercase', flexShrink:0 }}>Soon</span>
                </div>
              ))}
            </div>
            <a href="/contact" style={{ display: mob?'block':'inline-block', padding:'13px 28px', borderRadius:9, cursor:'pointer', background:'transparent', color:'rgba(139,92,246,.85)', border:'1px solid rgba(139,92,246,.35)', fontSize:14, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", textDecoration:'none', transition:'all .22s ease', textAlign:'center' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(139,92,246,.1)';e.currentTarget.style.transform='translateY(-2px)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.transform='none';}}
            >Register Early Interest →</a>
          </div>

        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ROOT
   ══════════════════════════════════════════════════════════════════════════ */
export default function Pricing() {
  return (
    <>
      <Helmet>
        <title>Pricing — RevUpAI | Pre-Built & Custom AI Roles</title>
        <meta name="description" content="RevUpAI's pre-built AI roles deploy in 2–4 weeks with predictable pricing. Custom AI roles scoped through a fixed-price Discovery. Secure AI and agentic process automation — no surprises." />
        <meta name="keywords" content="AI role pricing, enterprise AI cost, AI CISO pricing, AI DBA pricing, agentic AI pricing, secure AI cost, operated AI pricing, enterprise AI ROI" />
        <link rel="canonical" href="https://revupai.com/pricing" />
        <meta property="og:title" content="Pricing — Pre-Built & Custom AI Roles | RevUpAI" />
        <meta property="og:description" content="Pre-built AI roles deploy in 2–4 weeks. Custom roles scoped through Discovery. Predictable pricing." />
        <meta property="og:url" content="https://revupai.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing — RevUpAI AI Roles" />
        <meta name="twitter:description" content="Pre-built AI roles in 2–4 weeks. Custom roles scoped through Discovery." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
      </Helmet>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        * { box-sizing:border-box; }
      `}</style>
      <div style={{ background:BG, minHeight:'100vh' }}>
        <Hero/>
        <TwoTracks/>
        <ThreePaths/>
        <QualifierForm/>
        <ClosingBanner/>
      </div>
    </>
  );
}
